// useCaptureTick — the optional, mutable "capture tick" for the forming spine
// (003-narrate-voice-feedback, FR-016). Because Narrate is now deliberately
// quiet in voice (P2), a soft tick MAY confirm each landed step audibly. It is
// OPT-IN: OFF by default, toggled by a labelled control in SpineView.
//
// Audio approach (documented per the handoff): a tiny WebAudio oscillator
// "blip" rather than a bundled asset. This keeps audio OUT of the JS import
// graph (the spec's actual intent in FR-016) without committing a binary file
// to the repo, and works in a real browser. It is fully guarded for jsdom /
// any environment without an AudioContext and NEVER throws, so the tests do
// not depend on real playback. This hook imports no component
// (client-hooks-no-components).

import { useCallback, useRef, useState } from 'react';

// The Web Audio constructor, with the Safari-prefixed fallback. Typed locally
// so we never reach for `any`; absent in jsdom (guarded at call time).
type AudioContextCtor = new () => AudioContext;
interface AudioWindow {
  AudioContext?: AudioContextCtor;
  webkitAudioContext?: AudioContextCtor;
}

export interface UseCaptureTick {
  // Whether the audible tick is enabled. OFF by default (opt-in).
  soundOn: boolean;
  // Flip the preference. Enabling it lazily prepares the AudioContext.
  toggleSound: () => void;
  // Play one soft tick — a no-op when sound is OFF or audio is unavailable.
  // Safe to call on every landed step; never throws.
  playTick: () => void;
}

export function useCaptureTick(): UseCaptureTick {
  const [soundOn, setSoundOn] = useState(false);
  // Lazily-created, reused across ticks so we don't leak an AudioContext per
  // step. Held in a ref so re-renders don't recreate it.
  const ctxRef = useRef<AudioContext | null>(null);

  const ensureContext = useCallback((): AudioContext | null => {
    if (ctxRef.current) return ctxRef.current;
    if (typeof window === 'undefined') return null;
    const w = window as unknown as AudioWindow;
    const Ctor = w.AudioContext || w.webkitAudioContext;
    if (!Ctor) return null; // jsdom / unsupported: stay silent, never throw.
    try {
      ctxRef.current = new Ctor();
    } catch {
      ctxRef.current = null;
    }
    return ctxRef.current;
  }, []);

  const toggleSound = useCallback(() => {
    setSoundOn((on) => {
      const next = !on;
      // Prepare the context on the enabling click (a user gesture), which is
      // also when browsers allow audio to start.
      if (next) ensureContext();
      return next;
    });
  }, [ensureContext]);

  const playTick = useCallback(() => {
    if (!soundOn) return;
    const ctx = ensureContext();
    if (!ctx) return; // no audio in this environment — visual cue still fires.
    try {
      const now = ctx.currentTime;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = 880; // a soft, high-ish blip
      // Quick, gentle envelope so it reads as a tick, not a beep.
      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.exponentialRampToValueAtTime(0.06, now + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.12);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(now);
      osc.stop(now + 0.13);
    } catch {
      // Audio can be blocked (autoplay policy, suspended context); the spine's
      // visual + aria-live "heard" signal carries on regardless.
    }
  }, [soundOn, ensureContext]);

  return { soundOn, toggleSound, playTick };
}
